/* eslint-disable no-restricted-globals */
import type { FetcherOptions, NextRequest, TracingLogData } from "@/modules/fetch/types"

import { CF_RAY_HEADER, LOG_FETCH_PREFIX, REQUEST_ID_HEADER } from "@/modules/fetch/constants"
import { PAGE_TYPE_HEADER_NAME } from "@/modules/routing/routingManager/constants"

import { logger } from "./logger"

export const getTracingLogData = (nextRequest?: NextRequest): TracingLogData => {
  return nextRequest
    ? {
        page_type: (nextRequest.headers?.[PAGE_TYPE_HEADER_NAME] || "") as string,
        ray_id: (nextRequest.headers?.[CF_RAY_HEADER] || "") as string,
        request_id: (nextRequest.headers?.[REQUEST_ID_HEADER] || "") as string,
      }
    : {}
}

export async function fetchAndMonitor(
  url: string,
  requestInit: RequestInit,
  options: FetcherOptions
): Promise<Response> {
  if (typeof window === "undefined") {
    // To see `logger.debug` logs:
    // - on FE: https://github.com/jobteaser/next-jobteaser?tab=readme-ov-file#server-side
    // - locally: Set `LOG_LEVEL_SERVER=debug` in .env.local
    logger.debug({ requestInit }, `Server-side fetch details for "${url}"`)
  }

  const fetchPromise = fetch(url, requestInit)

  if (process.env.LOG_SERVER_FETCH !== "true" || typeof window !== "undefined") {
    return fetchPromise
  }

  const logMessage = `${LOG_FETCH_PREFIX}${url}`
  const start = new Date()
  const tracingLogData = getTracingLogData(options.nextRequest)

  return fetchPromise
    .then((...args) => {
      const stop = new Date()
      const duration = stop.valueOf() - start.valueOf()

      const [response] = args
      const loggingMethod = (response.status < 400 ? logger.info : logger.error).bind(logger)

      loggingMethod(
        {
          http_status: response.status,
          request_time: duration / 1000,
          request_url: response.url,
          ...tracingLogData,
        },
        logMessage
      )

      return Promise.resolve(...args)
    })
    .catch(err => {
      const stop = new Date()
      const duration = stop.valueOf() - start.valueOf()

      logger.error(
        {
          error: err.message,
          request_time: duration / 1000,
          request_url: url,
          ...tracingLogData,
        },
        logMessage
      )

      throw err
    })
}
